import React from "react";
import PropTypes from "prop-types";
import { Box, Button, ButtonGroup, LinearProgress } from "@mui/material";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ListIcon from "@mui/icons-material/List";
import { useFilters } from "components/Elements/Filters/FilterContext";

const ACTIVE_BG = "rgb(48,122,215)";
const HOVER_ACTIVE_BG = "rgb(48,122,215)";
const HOVER_INACTIVE_BG = "rgba(48,122,215,0.1)";

const TreeToolbar = ({
	onExpandAll,
	onCollapseAll,
	expandState = "neutral",
	isBulkLoading = false,
	disabled = false,
	onViewChange,
	testIdPrefix,
}) => {
	const { filterValues, updateFilter } = useFilters();

	const isTreeView = (filterValues.assetViewToggle?.value?.id || "tree") === "tree";

	const handleViewChange = (newView) => {
		const newViewValue = {
			id: newView,
			name: newView === "tree" ? "Tree View" : "List View",
		};
		updateFilter("assetViewToggle", newViewValue, { assetViewToggle: newView });
		onViewChange?.(newView === "tree");
	};

	const getButtonSx = (isActive) => ({
		minWidth: "40px",
		fontSize: 13,
		fontFamily: "Roboto Condensed",
		textTransform: "none",
		backgroundColor: isActive ? ACTIVE_BG : "transparent",
		"&:hover": {
			backgroundColor: isActive ? HOVER_ACTIVE_BG : HOVER_INACTIVE_BG,
		},
	});

	return (
		<Box>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "flex-end",
					gap: 12,
					padding: "8px 16px",
					borderBottom: "1px solid #e0e0e0",
					backgroundColor: "#fafafa",
				}}
			>
				<ButtonGroup size="small">
					<Button
						data-testid={testIdPrefix ? `${testIdPrefix}-tree-view` : undefined}
						onClick={() => handleViewChange("tree")}
						variant={isTreeView ? "contained" : "outlined"}
						sx={getButtonSx(isTreeView)}
					>
						<AccountTreeIcon sx={{ mr: 0.5 }} fontSize="small" />
						Tree
					</Button>
					<Button
						data-testid={testIdPrefix ? `${testIdPrefix}-list-view` : undefined}
						onClick={() => handleViewChange("list")}
						variant={!isTreeView ? "contained" : "outlined"}
						sx={getButtonSx(!isTreeView)}
					>
						<ListIcon sx={{ mr: 0.5 }} fontSize="small" />
						List
					</Button>
				</ButtonGroup>
				<ButtonGroup
					size="small"
					disabled={!isTreeView || disabled || isBulkLoading}
				>
					<Button
						data-testid={testIdPrefix ? `${testIdPrefix}-expand-all` : undefined}
						onClick={onExpandAll}
						variant={
							isTreeView && expandState === "expanded"
								? "contained"
								: "outlined"
						}
						sx={getButtonSx(isTreeView && expandState === "expanded")}
					>
						<UnfoldMoreIcon sx={{ mr: 0.5 }} fontSize="small" />
						Expand All
					</Button>
					<Button
						data-testid={testIdPrefix ? `${testIdPrefix}-collapse-all` : undefined}
						onClick={onCollapseAll}
						variant={
							isTreeView && expandState === "collapsed"
								? "contained"
								: "outlined"
						}
						sx={getButtonSx(isTreeView && expandState === "collapsed")}
					>
						<UnfoldLessIcon sx={{ mr: 0.5 }} fontSize="small" />
						Collapse All
					</Button>
				</ButtonGroup>
			</div>
			{isBulkLoading && <LinearProgress />}
		</Box>
	);
};

TreeToolbar.propTypes = {
	onExpandAll: PropTypes.func.isRequired,
	onCollapseAll: PropTypes.func.isRequired,
	expandState: PropTypes.oneOf(["expanded", "collapsed", "neutral"]),
	isBulkLoading: PropTypes.bool,
	disabled: PropTypes.bool,
	onViewChange: PropTypes.func,
	testIdPrefix: PropTypes.string,
};

export default React.memo(TreeToolbar);
